import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { extractApiError } from '../../lib/api';
import { createInspection, uploadPhotosParallel, UploadProgress } from '../../lib/inspections';
import { sitesApi, Site, isSiteSelectable, isSiteWritable } from '../../lib/organizations';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AlertTriangleIcon as ExclamationIcon,
  XIcon,
} from '../../components/icons';
import { Button, Card } from '@safetyhub/ui';

interface PhotoPreview {
  file: File;
  previewUrl: string;
}

// 사진 확대 보기 모달
interface PhotoLightboxProps {
  photos: PhotoPreview[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function PhotoLightbox({ photos, currentIndex, onClose, onNavigate }: PhotoLightboxProps) {
  const currentPhoto = photos[currentIndex];

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // 스크롤 방지
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex, photos.length, onClose, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 닫기 버튼 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white hover:text-ink-300 transition-colors z-10"
        aria-label="닫기"
      >
        <XIcon className="h-8 w-8" />
      </button>

      {/* 이미지 카운터 */}
      <div className="absolute top-4 left-4 text-white text-sm">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* 이전 버튼 */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 p-2 text-white hover:text-ink-300 transition-colors"
          aria-label="이전 사진"
        >
          <ChevronLeftIcon className="h-10 w-10" />
        </button>
      )}

      {/* 이미지 */}
      <div
        className="max-w-[90vw] max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentPhoto.previewUrl}
          alt={currentPhoto.file.name}
          className="max-w-full max-h-[80vh] object-contain"
        />
        <p className="mt-4 text-white text-sm">{currentPhoto.file.name}</p>
      </div>

      {/* 다음 버튼 */}
      {currentIndex < photos.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 p-2 text-white hover:text-ink-300 transition-colors"
          aria-label="다음 사진"
        >
          <ChevronRightIcon className="h-10 w-10" />
        </button>
      )}
    </div>
  );
}

export default function NewInspection() {
  const navigate = useNavigate();
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 폼 상태
  const [siteId, setSiteId] = useState('');
  const [notes, setNotes] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  // 사진 상태
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // UI 상태
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[] | null>(null);

  // 현장 목록 로드
  useEffect(() => {
    if (companyId) {
      loadSites();
    }
  }, [companyId]);

  // 컴포넌트 언마운트 시 미리보기 URL 정리
  useEffect(() => {
    return () => {
      photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    };
  }, []);

  const loadSites = async () => {
    if (!companyId) return;

    setIsSitesLoading(true);
    try {
      const data = await sitesApi.findAll(companyId);
      // 일시중지·종료 현장은 선택지에서 제외(생애주기 축)
      setSites(data.filter(isSiteSelectable));
    } catch {
      setError('현장 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsSitesLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: PhotoPreview[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // 이미지 파일만 허용
      if (!file.type.startsWith('image/')) continue;
      // 10MB 제한
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: 파일 크기가 10MB를 초과합니다.`);
        continue;
      }
      newPhotos.push({
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    setPhotos((prev) => [...prev, ...newPhotos]);
    // input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;

    const newPhotos: PhotoPreview[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: 파일 크기가 10MB를 초과합니다.`);
        continue;
      }
      newPhotos.push({
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // 라이트박스 핸들러
  const handleOpenLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const handleLightboxNavigate = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    if (!siteId) {
      setError('현장을 선택해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // 1. 점검 생성
      const response = await createInspection({
        companyId,
        siteId,
        notes: notes || undefined,
        scheduledAt: scheduledAt || undefined,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || '점검 생성에 실패했습니다.');
      }

      const inspectionId = response.data.id;

      // 2. 사진 업로드 (있는 경우) - 병렬 업로드로 빠르게 처리
      if (photos.length > 0) {
        setUploadProgress([]);
        const result = await uploadPhotosParallel(
          inspectionId,
          photos.map((p) => p.file),
          companyId,
          (progress) => setUploadProgress(progress),
        );

        // 업로드 실패한 파일이 있으면 알림
        if (result.failed.length > 0) {
          const failedNames = result.failed.map((f) => f.filename).join(', ');
          setError(`일부 사진 업로드 실패: ${failedNames}`);
        }
      }

      // 4. 상세 페이지로 이동
      navigate(`/inspections/${inspectionId}`);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  };

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900">새 점검 시작</h1>
        <p className="mt-1 text-sm text-ink-500">
          현장을 선택하고 사진을 업로드하여 새 점검을 시작합니다.
        </p>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-6 rounded-card bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError('')}
                className="inline-flex text-red-400 hover:text-red-500"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 현장 선택 */}
        <Card radius="lg" padding="xl" elevation="md">
          <label htmlFor="site" className="block text-sm font-medium text-ink-700 mb-2">
            현장 선택 <span className="text-red-500">*</span>
          </label>
          {isSitesLoading ? (
            <div className="flex items-center text-ink-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600 mr-2"></div>
              현장 목록 불러오는 중...
            </div>
          ) : sites.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-ink-500">등록된 현장이 없습니다.</p>
              <button
                type="button"
                onClick={() => navigate('/sites/new')}
                className="mt-2 text-sm text-brand-600 hover:text-brand-800"
              >
                현장 추가하기
              </button>
            </div>
          ) : (
            <select
              id="site"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm rounded-control"
            >
              <option value="">현장을 선택하세요</option>
              {sites.map((site) => {
                // 이관중 현장은 목록에는 남기되 고를 수 없다(CreateInspectionModal과 같은 결).
                const isPending = !isSiteWritable(site);
                return (
                  <option key={site.id} value={site.id} disabled={isPending}>
                    {site.name}
                    {isPending ? ' (이관대기)' : site.address ? ` - ${site.address}` : ''}
                  </option>
                );
              })}
            </select>
          )}
        </Card>

        {/* 예정일 (선택) */}
        <Card radius="lg" padding="xl" elevation="md">
          <label htmlFor="scheduledAt" className="block text-sm font-medium text-ink-700 mb-2">
            예정일 (선택)
          </label>
          <input
            type="datetime-local"
            id="scheduledAt"
            value={scheduledAt}
            max={new Date().toISOString().slice(0, 16)}
            onChange={(e) => {
              const newValue = e.target.value;
              setScheduledAt(newValue);

              // 날짜와 시간이 모두 설정되면 입력창 닫기
              // datetime-local 형식: YYYY-MM-DDTHH:MM
              if (newValue && newValue.length >= 16) {
                // 약간의 지연 후 blur하여 자연스러운 UX 제공
                setTimeout(() => {
                  e.target.blur();
                }, 100);
              }
            }}
            className="block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
          />
        </Card>

        {/* 사진 업로드 */}
        <Card radius="lg" padding="xl" elevation="md">
          <label className="block text-sm font-medium text-ink-700 mb-2">
            사진 업로드
          </label>
          <p className="text-sm text-ink-500 mb-4">
            현장 사진을 업로드하세요. 최대 10MB, 이미지 파일만 허용됩니다.
          </p>

          {/* 드래그 앤 드롭 영역 */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-ink-300 rounded-card p-6 text-center hover:border-brand-500 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon className="mx-auto h-12 w-12 text-ink-400" />
            <p className="mt-2 text-sm text-ink-600">
              클릭하거나 파일을 드래그하여 업로드
            </p>
            <p className="mt-1 text-xs text-ink-500">
              PNG, JPG, HEIC 지원 (최대 10MB)
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* 사진 미리보기 */}
          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative group">
                  <button
                    type="button"
                    onClick={() => handleOpenLightbox(index)}
                    className="w-full focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-card-sm"
                  >
                    <img
                      src={photo.previewUrl}
                      alt={`미리보기 ${index + 1}`}
                      className="h-24 w-full object-cover rounded-card-sm cursor-pointer hover:opacity-90 transition-opacity"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto(index);
                    }}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                  <p className="mt-1 text-xs text-ink-500 truncate">
                    {photo.file.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* 메모 */}
        <Card radius="lg" padding="xl" elevation="md">
          <label htmlFor="notes" className="block text-sm font-medium text-ink-700 mb-2">
            메모 (선택)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
            placeholder="점검에 대한 메모를 입력하세요..."
          />
        </Card>

        {/* 업로드 진행률 */}
        {uploadProgress && uploadProgress.length > 0 && (
          <div className="bg-brand-50 rounded-card p-4">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600 mr-2"></div>
              <span className="text-sm text-brand-700">
                사진 업로드 중... ({uploadProgress.filter((p) => p.status === 'completed').length}/{uploadProgress.length})
              </span>
            </div>
            <div className="mt-2 w-full bg-brand-200 rounded-full h-2">
              <div
                className="bg-brand-600 h-2 rounded-full transition-all"
                style={{
                  width: `${(uploadProgress.filter((p) => p.status === 'completed').length / uploadProgress.length) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            onClick={() => navigate('/inspections')}
            disabled={isLoading}
          >
            취소
          </Button>
          <div className="relative inline-block group">
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !siteId}
            >
              {isLoading ? '시작 중...' : '점검 시작'}
            </Button>
            {/* 비활성화 이유 툴팁 */}
            {(isLoading || !siteId) && (
              <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10 pointer-events-none">
                <div className="bg-ink-900 text-white text-xs rounded-card py-2 px-3 whitespace-nowrap shadow-lg">
                  {isLoading
                    ? '점검 생성 중입니다...'
                    : !siteId
                      ? '현장을 선택해주세요'
                      : ''}
                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-ink-900"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* 사진 확대 보기 모달 */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          currentIndex={lightboxIndex}
          onClose={handleCloseLightbox}
          onNavigate={handleLightboxNavigate}
        />
      )}
    </div>
  );
}

// Icons
function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
      />
    </svg>
  );
}

